import React, { useEffect, useState, useRef } from "react";
import AppButton from "../../../Common/AppButton";

export const DynamicKeyboard = ({
  question,
  onSubmit,
  enableSkip,
  isDisabled,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isBlanking, setIsBlanking] = useState(false);
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [placeholderRatio, setPlaceholderRatio] = useState("1 / 1");
  const preloadRef = useRef({ lastQuestionId: null, abort: false });

  const isImageUrl = (url) =>
    typeof url === "string" &&
    /\.(png|jpe?g|gif|bmp|webp)(\?.*)?$/i.test(url);

  const computeRatio = (numOpts) => {
    if (numOpts === 4) return "17 / 18";
    if (numOpts === 3) return "3 / 1";
    if (numOpts === 2) return "2 / 1";
    return "1 / 1";
  };

  useEffect(() => {
    const quesList = JSON.parse(localStorage.getItem("currentWord")) || [];
    const allImageUrls = quesList
      .flatMap((q) => q.options || [])
      .filter((opt) => isImageUrl(opt));

    allImageUrls.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (!question) return;

    const opts = question.options || [];
    const count = opts.length;
    const ratio = computeRatio(count);

    setPlaceholderCount(count);
    setPlaceholderRatio(ratio);
    setIsBlanking(true);

    const imageUrls = opts.filter((opt) => isImageUrl(opt));

    if (imageUrls.length === 0) {
      setIsBlanking(false);
      setSelectedIndex(null);
      preloadRef.current = { lastQuestionId: null, abort: false };
      return;
    }

    let loadedCount = 0;
    const thisQuestionId = JSON.stringify(opts);
    preloadRef.current.lastQuestionId = thisQuestionId;
    preloadRef.current.abort = false;

    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;

      const onFinish = () => {
        if (
          preloadRef.current.abort ||
          preloadRef.current.lastQuestionId !== thisQuestionId
        ) {
          return;
        }
        loadedCount += 1;
        if (loadedCount === imageUrls.length) {
          setIsBlanking(false);
          setSelectedIndex(null);
          preloadRef.current = { lastQuestionId: null, abort: false };
        }
      };

      img.onload = onFinish;
      img.onerror = onFinish;
    });

    return () => {
      preloadRef.current.abort = true;
    };
  }, [question]);

  const handleSubmit = (skipped) => {
    if (isDisabled) return;
    if (!skipped && selectedIndex === null) return;

    const opts = question.options || [];
    setPlaceholderCount(opts.length);
    setPlaceholderRatio(computeRatio(opts.length));
    setIsBlanking(true);

    const chosenValue = skipped ? null : opts[selectedIndex];
    onSubmit(chosenValue, skipped);

    setSelectedIndex(null);
  };

  if (isBlanking) {
    const gridCols = placeholderCount === 4 ? 2 : 1;

    return (
      <div className="p-4 pb-8 box-border w-full">
        <div
          className="grid gap-4 w-full"
          style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
        >
          {Array.from({ length: placeholderCount }).map((_, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                aspectRatio: placeholderRatio,
                border: "2px solid black",
                borderRadius: "0.375rem",
                backgroundColor: "#fff",
                userSelect: "none",
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const opts = question?.options || [];
  const cols = opts.length === 4 ? 2 : 1;
  const currentRatio = computeRatio(opts.length);

  return (
    <div className="p-4 pb-8 box-border w-full">
      <div
        className="grid gap-4 w-full"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {opts.map((opt, i) => {
          const isSelected = i === selectedIndex;
          return (
            <div
              key={i}
              onClick={() => !isDisabled && setSelectedIndex(i)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                aspectRatio: currentRatio,
                border: isSelected ? "2px solid #ccf900" : "2px solid black",
                borderRadius: "0.375rem",
                userSelect: "none",
                fontSize: "1rem",
                fontWeight: 500,
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
                backgroundColor: isSelected ? "#ccf900" : "#fff",
              }}
            >
              {isImageUrl(opt) ? (
                <img
                  src={opt}
                  alt={`option-${i}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{opt}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-center items-center mt-6 relative">
        <AppButton
          onClick={() => handleSubmit(false)}
          disabled={selectedIndex === null || isDisabled}
          className="w-48 h-12 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Submit
        </AppButton>

        {enableSkip && (
          <span
            onClick={() => handleSubmit(true)}
            className="absolute right-0 text-sm underline cursor-pointer"
          >
            Skip &gt;&gt;
          </span>
        )}
      </div>
    </div>
  );
};
